/**
 * Export utilities.
 *
 * CSV only. An `exportToExcel` helper used to live here, dynamically
 * importing `xlsx` inside a try/catch that fell back to CSV - but the
 * dependency was never declared, so the import always failed, the
 * fallback always ran, and no screen ever called the function. All it
 * produced was a "Can't resolve 'xlsx'" warning on every production
 * build.
 *
 * Adding real Excel export is a deliberate decision, not a silent
 * optional import: npm's `xlsx` is stale (SheetJS distributes elsewhere)
 * and carries known advisories, so a vetted dependency choice - and a
 * UI that actually offers the format - has to come first.
 */

/**
 * Convert array of objects to CSV string
 */
export const convertToCSV = (data, headers) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Use provided headers or extract from first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.map(header => {
    // Handle nested objects (e.g., customer_name)
    if (typeof header === 'object' && header.key) {
      return header.label || header.key;
    }
    return header;
  }).join(',');

  // Create data rows
  const dataRows = data.map(row => {
    return csvHeaders.map(header => {
      let value;
      if (typeof header === 'object' && header.key) {
        value = getNestedValue(row, header.key);
      } else {
        value = row[header];
      }
      
      // Handle null/undefined/empty objects
      if (value === null || value === undefined || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return '';
      }
      
      // Convert to string and escape commas/quotes
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Get nested value from object using dot notation
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, prop) => {
    return current && current[prop] !== undefined ? current[prop] : '';
  }, obj);
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Export data to CSV
 */
export const exportToCSV = (data, filename, headers = null) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const csvContent = convertToCSV(data, headers);
  downloadCSV(csvContent, filename);
};


